/**
 * Admin Pricing — Types DTO (alignés avec le backend Agent L).
 *
 * Endpoints consommés :
 *   - GET    /api/v1/admin/pricing                 → ListCurrentPricingController
 *   - POST   /api/v1/admin/pricing                 → PublishPricingController
 *   - DELETE /api/v1/admin/pricing/{pricingId}     → SupersedePricingController
 *   - GET    /api/v1/admin/pricing/history         → GetPricingHistoryController
 *   - POST   /api/v1/admin/pricing/simulate        → SimulateMonthlyRevenueController
 *
 * Accès : ROLE_PLATFORM_ADMIN (super-utilisateur plateforme).
 */

/** PricingProvider enum — aligné avec apps/api/src/Admin/Pricing/Domain/ValueObject/PricingProvider.php */
export const PRICING_PROVIDERS = [
  'openai_whisper',
  'openai_tts',
  'anthropic_claude',
  'aws_s3_storage',
  'platform_plan',
] as const;
export type PricingProvider = (typeof PRICING_PROVIDERS)[number];

/** PricingTier enum — aligné avec le backend. */
export const PRICING_TIERS = ['any', 'free', 'starter', 'team', 'enterprise'] as const;
export type PricingTier = (typeof PRICING_TIERS)[number];

/** UnitType enum — aligné avec le backend. */
export const UNIT_TYPES = [
  'input_token',
  'output_token',
  'audio_minute',
  'tts_character',
  'gb_month',
  'monthly_flat',
] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

/** Libellés lisibles pour l'UI (miroir des `label()` PHP). */
export const PROVIDER_LABELS: Record<PricingProvider, string> = {
  openai_whisper: 'OpenAI Whisper (STT)',
  openai_tts: 'OpenAI TTS-1',
  anthropic_claude: 'Anthropic Claude Sonnet 4.6',
  aws_s3_storage: 'AWS S3 Storage',
  platform_plan: 'Feedback Orchestrator Plan',
};

export const TIER_LABELS: Record<PricingTier, string> = {
  any: 'Any tier',
  free: 'Free',
  starter: 'Starter',
  team: 'Team',
  enterprise: 'Enterprise',
};

export const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  input_token: 'per 1M input tokens',
  output_token: 'per 1M output tokens',
  audio_minute: 'per audio minute',
  tts_character: 'per 1M TTS characters',
  gb_month: 'per GB-month',
  monthly_flat: 'per month (flat)',
};

/** Une ligne de tarification courante renvoyée par GET /api/v1/admin/pricing. */
export interface PricingRow {
  id: string;
  provider: PricingProvider;
  providerLabel: string;
  tier: PricingTier;
  tierLabel: string;
  unitType: UnitType;
  unitTypeLabel: string;
  priceCents: number;
  priceUsd: number;
  currency: string;
  description: string | null;
  effectiveFrom: string;
}

/** Entrée d'historique renvoyée par GET /api/v1/admin/pricing/history. */
export interface PricingHistoryEntry {
  id: string;
  priceCents: number;
  priceUsd: number;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  supersededBy: string | null;
  createdBy: string | null;
  description: string | null;
}

/** Payload de création — POST /api/v1/admin/pricing. */
export interface PublishPricingPayload {
  provider: PricingProvider;
  tier: PricingTier;
  unitType: UnitType;
  priceCents: number;
  effectiveFrom: string; // ISO 8601
  currency?: string;
  description?: string | null;
}

/** Réponse POST /api/v1/admin/pricing (201). */
export interface PublishPricingResponse {
  id: string;
  provider: string;
  tier: string;
  unitType: string;
  priceCents: number;
  currency: string;
  effectiveFrom: string;
  supersededPricingId: string | null;
}

/** Payload simulation — POST /api/v1/admin/pricing/simulate. */
export interface SimulateRevenuePayload {
  customerCountByTier: Partial<Record<Exclude<PricingTier, 'any'>, number>>;
  avgUsageByUnit: Partial<Record<UnitType, number>>;
}

/** Résultat simulation. */
export interface SimulateRevenueResult {
  grossRevenueCents: number;
  upstreamCostCents: number;
  netMarginCents: number;
  marginPct: number;
  revenueBreakdownByTier: Record<string, number>;
  costBreakdownByProvider: Record<string, number>;
}
